import * as React from "react";
import cloneDeep from "lodash/cloneDeep";

import { useState, useEffect, useRef } from "react";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import { mobileBreakpoint } from "../../../../entrypoints/theme";
import { ProductTile } from "@project-stories/ecommerce/Product/ProductTile";
import PromotionTile from "@project-stories/ecommerce/PromotionalTile/PromotionTile";
import Breadcrumbs from "../Product/Breadcrumbs";
import FiltersSidebar from "@src/stories/ecommerce/Collection/FiltersSidebar";
import CollectionToolbar from "@project-stories/ecommerce/Collection/CollectionToolbar";
import LoadingSpinner from "@src/stories/elements/Spinner/LoadingSpinner";

export const Collection = ({ settings }) => {
  const [
    displayedProductsWithPromoBlocks,
    setDisplayedProductsWithPromoBlocks,
  ] = useState([]);
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState([]);
  const isMobile = useMobileBreakpoint(mobileBreakpoint);
  const [isSidebarVisible, setSidebarVisible] = useState(false);

  const sortedPromotionBlocks = settings.promotion_blocks.sort((a, b) => {
    return b.sort_order - a.sort_order;
  });
  const promoBlocks = useRef(sortedPromotionBlocks);
  const paginationHaveLoaded = useRef(false);
  const isFiltering = useRef(false);


  const initialized = useRef(false);
  // const adjustedProductsPerPage = useRef(settings.products_per_page);

  const toggleSidebar = () => {
    setSidebarVisible((prevState) => {
      const newState = !prevState;
      document.body.classList.toggle("no-scroll__filters", newState);
      return newState;
    });
  };

  const {
    columns_mobile,
    columns_desktop,
    column_gap_mobile,
    column_gap_desktop,
    row_gap_mobile,
    row_gap_desktop,
    id,
  } = settings.section;

  const handleProductsResults = (event) => {
    if (event.detail.type === "plp") {
      window.dispatchEvent(
        new CustomEvent("hide-spinner", {
          detail: {
            id: "spinner-collection",
          },
        }),
      );

      isFiltering.current = event.detail.isFiltering;

      if (isFiltering.current) {
        setProducts(event.detail.products);

        if (paginationHaveLoaded.current) {
          window.dispatchEvent(
            new CustomEvent("current-products-updated", {
              detail: {
                totalViewed: event.detail.products.length,
              },
            }),
          );
        } else {
          const intervalId = setInterval(() => {
            if (paginationHaveLoaded.current) {
              window.dispatchEvent(
                new CustomEvent("current-products-updated", {
                  detail: {
                    totalViewed: event.detail.products.length,
                  },
                }),
              );

              clearInterval(intervalId);
            }
          }, 100);
        }
      }

      const { products, page } = event.detail;

      if (!isFiltering.current) {
        isFiltering.current === false;
        const currentPage = page + 1;
        const totalProductsToShow = currentPage * settings.products_per_page;

        const promoBlocksToInsert = getPromoBlocksToBeInserted(totalProductsToShow);
        const actualProductsNeeded = totalProductsToShow;

        const productsToBeAdded = getProductsFromLoadedProducts(
          products,
          actualProductsNeeded,
        );

        const updatedDisplayedProductsWithPromoBlocks = insertPromoBlocks(
          productsToBeAdded,
          promoBlocksToInsert,
        );
        setDisplayedProductsWithPromoBlocks(
          updatedDisplayedProductsWithPromoBlocks,
        );

        if (paginationHaveLoaded.current) {
          window.dispatchEvent(
            new CustomEvent("current-products-updated", {
              detail: {
                totalViewed: productsToBeAdded.length,
              },
            }),
          );
        } else {
          const intervalId = setInterval(() => {
            if (paginationHaveLoaded.current) {
              window.dispatchEvent(
                new CustomEvent("current-products-updated", {
                  detail: {
                    totalViewed: productsToBeAdded.length,
                  },
                }),
              );

              clearInterval(intervalId);
            }
          }, 100);
        }
      }
    }
  };

  function handlePaginationLoad() {
    paginationHaveLoaded.current = true;
  }

  const handleSortResults = (event) => {
    // Sort functionality would be implemented here if needed
  };

  const handleFiltersResults = (event) => {
    if (event.detail.type === "plp") {
      if (event.detail.filters) {
        setFilters(event.detail.filters);
      }
    }
  };

  const handleEscapeKey = (event) => {
    if (event.key === "Escape") {
      setSidebarVisible(false);
    }
  };

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;

      window.dispatchEvent(new CustomEvent("collection-search-loaded"));

      window.addEventListener("pagination-have-loaded", handlePaginationLoad);
      window.addEventListener("product-list", handleProductsResults);
      window.addEventListener("filter-list", handleFiltersResults);
      window.addEventListener("keydown", handleEscapeKey);

      // Cleanup the event listeners on component unmount
      return () => {
        // window.removeEventListener("product-list", handleProductsResults);
        // window.removeEventListener("filter-list", handleFiltersResults);
        window.removeEventListener("keydown", handleEscapeKey);
      };
    }
  }, []);

  const getProductsFromLoadedProducts = (products, nextPageCount) => {
    return products.slice(0, nextPageCount);
  };

  const insertPromoBlocks = (productsToBeAdded, promoBlocksToInsert) => {
    const finalGrid: any[] = [];
    let productIndex = 0;

    const maxPosition = Math.max(
      productsToBeAdded.length - 1,
      ...promoBlocksToInsert.map(pb => pb.position)
    );

    for (let gridIndex = 0; gridIndex <= maxPosition; gridIndex++) {
      const promoAtPosition = promoBlocksToInsert.find(pb => pb.position === gridIndex);
      
      if (promoAtPosition) {
        finalGrid.push(promoAtPosition.block);
      } else if (productIndex < productsToBeAdded.length) {
        finalGrid.push(productsToBeAdded[productIndex]);
        productIndex++;
      }
    }
    
    return finalGrid;
  };

  const getPromoBlocksToBeInserted = (totalProductsDisplayed) => {
    const blocksToInsert: any = [];

    promoBlocks.current.forEach((block) => {
      // Ensure block.position is an array before processing
      const positions = Array.isArray(block.position) ? block.position : [block.position].filter(Boolean);
      
      positions.sort((a, b) => a - b);
      positions.forEach((position) => {
        if (position <= totalProductsDisplayed + blocksToInsert.length + 1) {
          blocksToInsert.push({
            position: position - 1,
            block,
          });
        }
      });
    });

    return blocksToInsert.sort((a, b) => a.position - b.position);
  };


  return (
    <div x-init={`initialise('${settings.collection.handle}', true)`}>
      <FiltersSidebar
        isVisible={isSidebarVisible}
        filters={filters}
        metaobject_filters={settings.filters}
        toggleSidebar={toggleSidebar}
      />
      <div className="flex items-center justify-between mb-4 collection__toolbar">
        {!isMobile && (
          <Breadcrumbs
            templateName={settings.template.name}
            shop={settings.shop}
            collection={settings.collection}
            product={null}
            routes={settings.routes}
            blog={null}
            article={null}
            page={settings.page}
            request={null}
            pageTitle={null}
          />
        )}
        <CollectionToolbar toggleSidebar={toggleSidebar} />
      </div>
      <div
        id="product-grid"
        className={`collection__product-grid relative grid grid-cols-${columns_mobile} lg:grid-cols-${columns_desktop} gap-x-[${column_gap_mobile}px] lg:gap-x-[${column_gap_desktop}px] gap-y-[${row_gap_mobile}px] lg:gap-y-[${row_gap_desktop}px] auto-cols-max`}
        data-id={id}
      >
        <LoadingSpinner id="spinner-collection" />

        {isFiltering.current ? (
          <>
            {products.map((block, index) => (
              <React.Fragment key={index}>
                <div className={"collection-tile"}>
                  <ProductTile product={block} siblings={[]} />
                </div>
              </React.Fragment>
            ))}
          </>
        ) : (
          <>
            {displayedProductsWithPromoBlocks.map((block, index) => (
              <React.Fragment key={index}>
                {block.type === "promo" ? (
                  <PromotionTile promotion={block} sectionSettings={settings.section} />
                ) : (
                  <div className={"collection-tile"}>
                    <ProductTile product={block} siblings={[]} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default Collection;
